import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import { Users, Plus, Pencil, Trash2, Mail, Phone, Euro, ShieldCheck, Loader2 } from 'lucide-react';

const QUALIFICHE = ['RSPP', 'CSP', 'CSE', 'Ingegnere', 'Architetto', 'Tecnico abilitato'];

export default function AdminProfessionisti() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAdmin = user && (user.role === 'admin' || ['demo@cantiere.ai', 'alessandroscarpuzza@gmail.com'].includes((user.email || '').toLowerCase()));

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openForm, setOpenForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const empty = { nome: '', qualifica: 'RSPP', email: '', telefono: '', costo_per_pratica: 0, note: '', attivo: true, qualifiche_extra: [] };
    const [form, setForm] = useState(empty);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/professionisti');
            setItems(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setLoading(false); }
    };
    useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin]);

    if (!isAdmin) {
        return (
            <div className="p-10 text-center text-[hsl(var(--muted-foreground))]" data-testid="admin-only">
                <ShieldCheck className="w-12 h-12 mx-auto mb-3" />
                <p className="text-lg font-semibold">{t('admin.admin_only', 'Accesso riservato agli amministratori')}</p>
            </div>
        );
    }

    const openNew = () => { setEditing(null); setForm(empty); setOpenForm(true); };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            nome: p.nome || '',
            qualifica: p.qualifica || 'RSPP',
            email: p.email || '',
            telefono: p.telefono || '',
            costo_per_pratica: p.costo_per_pratica || 0,
            note: p.note || '',
            attivo: p.attivo !== false,
            qualifiche_extra: p.qualifiche_extra || [],
        });
        setOpenForm(true);
    };

    const save = async (e) => {
        e?.preventDefault();
        if (!form.nome || !form.email || !form.qualifica) {
            toast.error(t('pages.adm_pro_fill_required', 'Compila i campi obbligatori'));
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                costo_per_pratica: parseFloat(form.costo_per_pratica) || 0,
            };
            if (editing) {
                await api.put(`/admin/professionisti/${editing.id}`, payload);
                toast.success(t('common.saved', 'Salvato'));
            } else {
                await api.post('/admin/professionisti', payload);
                toast.success(t('pages.adm_pro_created', 'Professionista creato'));
            }
            setOpenForm(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setSaving(false); }
    };

    const del = async (p) => {
        if (!window.confirm(t('pages.adm_pro_confirm_delete', { name: p.nome }) || `Eliminare ${p.nome}?`)) return;
        try {
            await api.delete(`/admin/professionisti/${p.id}`);
            toast.success(t('common.deleted', 'Eliminato'));
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    const toggleQualificaExtra = (q) => {
        const has = (form.qualifiche_extra || []).includes(q);
        setForm({
            ...form,
            qualifiche_extra: has
                ? form.qualifiche_extra.filter((x) => x !== q)
                : [...(form.qualifiche_extra || []), q],
        });
    };

    return (
        <div data-testid="admin-professionisti-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-4xl md:text-5xl flex items-center gap-3">
                        <Users className="w-9 h-9 text-[hsl(var(--accent))]" />
                        {t('pages.adm_pro_title', 'Network Professionisti')}
                    </h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.adm_pro_subtitle', 'Gestisci RSPP, CSP/CSE, ingegneri e tecnici abilitati per la firma DVR')}</p>
                </div>
                <Button onClick={openNew} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold gap-2 h-11" data-testid="admin-pro-new-button">
                    <Plus className="w-4 h-4" /> {t('pages.adm_pro_new', 'Aggiungi')}
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-10"><Loader2 className="w-8 h-8 mx-auto animate-spin text-[hsl(var(--accent))]" /></div>
            ) : items.length === 0 ? (
                <Card className="p-10 text-center text-[hsl(var(--muted-foreground))]" data-testid="admin-pro-empty">
                    <Users className="w-12 h-12 mx-auto mb-3" />
                    <p className="mb-3">{t('pages.adm_pro_empty', 'Nessun professionista configurato')}</p>
                    <Button onClick={openNew} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]"><Plus className="w-4 h-4 mr-1" /> {t('pages.adm_pro_new', 'Aggiungi il primo')}</Button>
                </Card>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((p) => (
                        <Card key={p.id} className="p-5 flex flex-col" data-testid={`admin-pro-card-${p.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold truncate">{p.nome}</h3>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        <Badge className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] text-xs">{p.qualifica}</Badge>
                                        {(p.qualifiche_extra || []).map((q, i) => (
                                            <Badge key={i} variant="outline" className="text-xs">{q}</Badge>
                                        ))}
                                    </div>
                                </div>
                                {!p.attivo && <Badge variant="outline" className="bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] text-xs">{t('common.inactive', 'Inattivo')}</Badge>}
                            </div>
                            <div className="space-y-1.5 text-sm text-[hsl(var(--muted-foreground))] flex-1">
                                <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{p.email}</span></div>
                                {p.telefono && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {p.telefono}</div>}
                                <div className="flex items-center gap-2"><Euro className="w-3.5 h-3.5" /> €{Number(p.costo_per_pratica || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })} {t('pages.adm_pro_per_practice_label', 'per pratica')}</div>
                            </div>
                            {p.note && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2 line-clamp-2 italic">{p.note}</p>}
                            <div className="flex gap-2 mt-3 pt-3 border-t border-[hsl(var(--border))]">
                                <Button onClick={() => openEdit(p)} size="sm" variant="outline" className="flex-1 gap-1" data-testid={`admin-pro-edit-${p.id}`}><Pencil className="w-3 h-3" /> {t('common.edit', 'Modifica')}</Button>
                                <Button onClick={() => del(p)} size="sm" variant="outline" className="text-[hsl(var(--destructive))] hover:bg-[hsl(0_80%_95%)]" data-testid={`admin-pro-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={openForm} onOpenChange={setOpenForm}>
                <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="admin-pro-form-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            {editing ? (t('pages.adm_pro_edit_title', 'Modifica professionista')) : (t('pages.adm_pro_new_title', 'Nuovo professionista'))}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4 mt-2">
                        <div>
                            <Label>{t('pages.adm_pro_name', 'Nome completo')} <span className="text-[hsl(var(--destructive))]">*</span></Label>
                            <Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ing. Mario Rossi" className="mt-1" data-testid="admin-pro-nome" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>{t('pages.adm_pro_qualifica_main', 'Qualifica principale')} <span className="text-[hsl(var(--destructive))]">*</span></Label>
                                <Select value={form.qualifica} onValueChange={(v) => setForm({ ...form, qualifica: v })}>
                                    <SelectTrigger className="mt-1" data-testid="admin-pro-qualifica"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {QUALIFICHE.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>{t('pages.adm_pro_cost', 'Costo per pratica (€)')}</Label>
                                <Input type="number" step="0.01" value={form.costo_per_pratica} onChange={(e) => setForm({ ...form, costo_per_pratica: e.target.value })} placeholder="80.00" className="mt-1" data-testid="admin-pro-costo" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">{t('pages.adm_pro_qualifiche_extra', 'Qualifiche aggiuntive')}</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {QUALIFICHE.filter((q) => q !== form.qualifica).map((q) => {
                                    const active = (form.qualifiche_extra || []).includes(q);
                                    return (
                                        <button
                                            key={q}
                                            type="button"
                                            onClick={() => toggleQualificaExtra(q)}
                                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] border-[hsl(var(--accent))]' : 'bg-transparent border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]'}`}
                                        >{q}</button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Email <span className="text-[hsl(var(--destructive))]">*</span></Label>
                                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="rossi@studio.it" className="mt-1" data-testid="admin-pro-email" />
                            </div>
                            <div>
                                <Label>{t('pages.adm_pro_phone', 'Telefono')}</Label>
                                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39..." className="mt-1" data-testid="admin-pro-telefono" />
                            </div>
                        </div>
                        <div>
                            <Label>{t('pages.adm_pro_note', 'Note')}</Label>
                            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t('pages.adm_pro_note_ph', 'Specialità, zone coperte, disponibilità...')} className="mt-1" />
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--muted))]/40">
                            <Label className="cursor-pointer">{t('pages.adm_pro_active', 'Professionista attivo (riceve assegnazioni)')}</Label>
                            <Switch checked={form.attivo} onCheckedChange={(v) => setForm({ ...form, attivo: v })} data-testid="admin-pro-attivo" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpenForm(false)}>{t('common.cancel', 'Annulla')}</Button>
                            <Button type="submit" disabled={saving} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold gap-2" data-testid="admin-pro-save-button">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                {editing ? (t('common.save', 'Salva')) : (t('common.create', 'Crea'))}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
